import axios, {
  AxiosError,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from 'axios'

import {
  ensureValidAccessToken,
  getAccessToken,
  getRefreshToken,
  isAuthRefreshRequest,
  refreshAccessToken,
  sessionExpired,
} from '@/lib/auth/authSession'

function setAuthorizationHeader(
  config: InternalAxiosRequestConfig,
  token: string
): void {
  if (!config.headers) {
    config.headers = new AxiosHeaders()
  }
  config.headers.set('Authorization', `Bearer ${token}`)
}

export function setupAxiosAuth(): void {
  axios.interceptors.request.use(async (config) => {
    if (config._skipAuthRefresh || isAuthRefreshRequest(config.url)) {
      return config
    }

    if (!getAccessToken() && !getRefreshToken()) {
      return config
    }

    const token = await ensureValidAccessToken()
    if (token) {
      setAuthorizationHeader(config, token)
    }

    return config
  })

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      if (
        !originalRequest ||
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest._skipAuthRefresh ||
        isAuthRefreshRequest(originalRequest.url)
      ) {
        return Promise.reject(error)
      }

      if (!getAccessToken() && !(await ensureValidAccessToken())) {
        sessionExpired()
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        const token = await refreshAccessToken()
        setAuthorizationHeader(originalRequest, token)
        return axios(originalRequest)
      } catch {
        sessionExpired()
        return Promise.reject(error)
      }
    }
  )
}
